import { useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import { useSelector } from 'react-redux';
import { ERROR_CODES } from '../constants';
import axiosConfig from '../store/sagas/axiosConfig';

// Custom hook to attach a global 401 Unauthorized interceptor to Axios.
// Clears expired PAC cookie and reloads page to show password access screen.
const useAxios401Interceptor = (router, track) => {
  const interceptorIdRef = useRef(null);
  const isHandling401Ref = useRef(false);
  const travelAgency = useSelector((store) => store.user.travelAgency);
  const doPasswordProtectClientFacingPage = travelAgency?.doPasswordProtectClientFacingPage;

  useEffect(() => {
    if (interceptorIdRef.current !== null) {
      return undefined;
    }

    interceptorIdRef.current = axiosConfig.interceptors.response.use(
      (response) => response,
      (error) => {
        try {
          const is401Error = error?.response?.status === 401;
          // Backend tags only password protection 401s. Other 401s (e.g. trip confirmation guest
          // verification) are handled by their callers, reloading on them would loop.
          const isPacRequired = error?.response?.data?.code === ERROR_CODES.PAC_REQUIRED;

          if (!doPasswordProtectClientFacingPage) {
            return Promise.reject(error);
          }

          if (is401Error && isPacRequired) {
            if (isHandling401Ref.current) {
              return Promise.reject(error);
            }

            isHandling401Ref.current = true;

            // Clear PAC cookie
            document.cookie = 'twpac=; Max-Age=0; path=/';

            // Track analytics
            if (track) {
              try {
                track('UNAUTHORIZED_401', {
                  path: router?.asPath || window?.location?.pathname,
                  url: error.config?.url,
                });
              } catch (e) {
                console.error('Analytics tracking failed:', e);
              }
            }

            const TIMEOUT = 3000;

            if (typeof window !== 'undefined') {
              toast.error('Your session has expired. Redirecting to login...', {
                duration: TIMEOUT,
                id: 'session-expired',
              });

              setTimeout(() => {
                window.location.reload();
              }, TIMEOUT);
            }
          }
        } catch (e) {
          console.error('401 interceptor error:', e);
        }

        return Promise.reject(error);
      },
    );

    return () => {
      if (interceptorIdRef.current !== null) {
        axiosConfig.interceptors.response.eject(interceptorIdRef.current);
        interceptorIdRef.current = null;
      }
      isHandling401Ref.current = false;
    };
  }, [router, track, doPasswordProtectClientFacingPage]);
};

export default useAxios401Interceptor;
